import type { PropsWithChildren } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { colorsPalettes, fonts, setFontVariant } from "@similarweb/styles";
import { IconButton, PlainTooltip } from "@similarweb/ui-components";

import { getIndex } from "app/components/React/Table/cells/IndexCell";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import { loadCustomSegmentsMetadata } from "app/pages/segments/config/segmentsConfigHelpers";
import { resolveTrackingCustomAttributesBase } from "app/pages/segments/redesigned-wizard/segment-wizard/utils/resolveTrackingCustomAttributes";
import { SegmentsStartTableAdditionalOptionsEllipsis } from "app/pages/segments/components/SegmentsStartTableAdditionalOptionsEllipsis";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { SwTrack } from "app/services/SwTrack";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { CoreSegmentCell } from "pro-features/components/core cells/src/CoreSegmentCell/CoreSegmentCell";
import { DefaultCell } from "pro-features/components/Workspace/TableCells/DefaultCell";
import { IndexCell } from "../../../components/React/Table/cells/index";
import { DefaultCellHeader } from "../../../components/React/Table/headerCells/index";
import { allTrackers } from "../../../services/track/track";
import { showSegmentsMeteringHook } from "../metering/SegmentsMetering";

import { MAX_SELECTED_ROWS, ROWS_PER_PAGE } from "./constants/common";
import { itsMyOwnSegment } from "./itsMyOwnSegment";
import { SegmentCellContainer } from "./segmentsGroupsAnalysisTableContainer/StyledComponents";
import type { IRow } from "./types";
import { getSegmentWizardRoute } from "./utils/routing";

const Capitalize = styled.div`
    text-transform: capitalize;
`;

const StyledSegmentCellContainer = styled(SegmentCellContainer)`
    cursor: pointer;
`;

const DateModifiedWrapper = styled.div`
    cursor: pointer;
`;

export const AnalyzeLable = styled(FlexColumn)<{ isDisabled?: boolean }>`
    padding: 0 16px;
    ${setFontVariant("body2", colorsPalettes.blue[400])};
    color: ${({ isDisabled }) => isDisabled && colorsPalettes.carbon[200]};
    cursor: ${({ isDisabled }) => (isDisabled ? "not-allowed" : "pointer")};
`;

const OwnerLabel = styled.span`
    letter-spacing: 0.6px;
    font-family: ${fonts.$robotoFontFamily};
    font-size: 8px;
    border-radius: 10px;
    margin: 3px 0 0 9px;
    font-weight: bold;
    color: #fff;
    text-transform: uppercase;
    padding: 4px 5px 3px 5px;
    height: 15px;
    line-height: 1;
    background-color: ${colorsPalettes.midnight[200]};
`;

const EmptyBtn = styled.span`
    width: 40px;
`;

const BlacklistedTooltip = ({ children }: PropsWithChildren) => (
    <PlainTooltip
        tooltipContent={translate("segment.analysis.org.table.column.disabled.tooltip")}
        placement={"top"}
    >
        <div className={"segments-table-row-disabled-active"}>{children}</div>
    </PlainTooltip>
);

const onLinkRowClick = (row: IRow) => {
    if (!row) {
        return;
    }

    const isBlacklisted = !!row.isBlacklisted;
    const isDisabled = row?.segmentAdvancedDisabled || isBlacklisted;

    if (isDisabled) {
        return;
    }

    allTrackers.dispatchTrackEvent("Internal link", "click", "SegmentAnalysis/Analyze segment", {
        Segment_Domain: row.domain,
        Segment_Name: row.segmentName,
        Segment_ID: row.id,
        Segment_Type: "custom",
    });

    const currentModule = proNavigator.getCurrentModule();
    const segmentAnalysisStateName = `${currentModule}-analysis-traffic`;

    proNavigator.go(segmentAnalysisStateName, {
        id: row.id,
        country: 840,
        mode: MODE.single,
        duration: "6m",
        ignoreFilterConsistency: true,
        ...SegmentsUtils.getPageFilterParams(row.id),
    });
};

const rowSelection = (supportSelectAll = false) => ({
    cellComponent: (props) => {
        const { row } = props;
        const isBlacklisted = !!row.isBlacklisted;
        const selectable = !isBlacklisted && row.selectable;
        const BLACK_LISTED_TOOLTIP_KEY = "segment.analysis.org.table.column.disabled.tooltip";
        const disabledTooltipKey = "segment.analysis.homepage.table.row.selection.disabled";
        const tooltipText = isBlacklisted
            ? translate(BLACK_LISTED_TOOLTIP_KEY)
            : translate(disabledTooltipKey, { limit: MAX_SELECTED_ROWS });
        return (
            <RowSelectionConsumer
                {...props}
                row={{ ...props.row, selectable, tooltipText }}
            />
        );
    },
    headerComponent: (props) =>
        !supportSelectAll ? (
            DefaultCellHeader
        ) : (
            <SelectAllRowsHeaderCellConsumer
                {...props}
                rowsPerPage={ROWS_PER_PAGE}
            />
        ),
    disableHeaderCellHover: true,
    sortable: false,
    visible: true,
    fixed: true,
    width: 48,
    isResizable: false,
});
const index = {
    field: "#",
    fixed: true,
    sortDirection: "desc",
    cellComponent: (props) => {
        const { row } = props;
        const isBlacklisted = !!row.isBlacklisted;
        return isBlacklisted ? (
            <BlacklistedTooltip>
                <IndexCell {...props} />
            </BlacklistedTooltip>
        ) : (
            <IndexCell {...props} />
        );
    },
    headerComponent: DefaultCellHeader,
    disableHeaderCellHover: true,
    sortable: false,
    width: 50,
    visible: true,
    isResizable: false,
};

const CellWrapper = styled.div`
    height: 100%;
`;

const domain = {
    field: "domain",
    get displayName() {
        return translate("segment.analysis.table.column.segments");
    },
    type: "string",
    format: "None",
    sortable: true,
    isSorted: false,
    cellComponent: ({ value, row }) => {
        const subtitleFilters = [];
        const editSegmentClick = (e) => {
            e.stopPropagation();
            SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${row.id}`);
            const segmentWizardStateName = getSegmentWizardRoute(proNavigator.getCurrentModule());
            proNavigator.go(segmentWizardStateName, { sid: row.id });
        };
        subtitleFilters.push({
            filter: "text",
            value: row.segmentName,
        });

        const isBlacklisted = !!row.isBlacklisted;
        const cell = (
            <CellWrapper
                className="py-0"
                onClick={() => onLinkRowClick(row)}
            >
                <CoreSegmentCell
                    withWebsiteSign={true}
                    segmentName={row.segmentName}
                    lastModified={row.lastUpdated}
                    isOrgSegment={!itsMyOwnSegment(row.userId)}
                    onClick={editSegmentClick}
                    segmentId={row.id}
                    icon={row.favicon}
                    domain={value}
                    subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                    className={isBlacklisted ? "segments-table-row-disabled" : ""}
                />
            </CellWrapper>
        );

        return isBlacklisted ? <BlacklistedTooltip>{cell}</BlacklistedTooltip> : cell;
    },
    headerComponent: DefaultCellHeader,
    sortDirection: "desc",
    totalCount: true,
    get tooltip() {
        return translate("segment.analysis.table.column.segments.tooltip");
    },
    showTotalCount: true,
    visible: true,
    width: 360,
    fixed: true,
    isResizable: false,
};
const lastUpdated = {
    field: "lastUpdated",
    get displayName() {
        return translate("segment.analysis.table.column.creationdate");
    },
    type: "date",
    sortable: true,
    isSorted: true,
    cellComponent: ({ value, row }) => {
        const isBlacklisted = !!row.isBlacklisted;

        const compToRender = () => (
            <DateModifiedWrapper onClick={() => onLinkRowClick(row)}>
                <DefaultCell className={isBlacklisted ? "segments-table-row-disabled" : ""}>
                    {dayjs(value).format("MMMM DD, YYYY")}
                </DefaultCell>
            </DateModifiedWrapper>
        );

        return isBlacklisted ? <BlacklistedTooltip>{compToRender()}</BlacklistedTooltip> : compToRender();
    },
    headerComponent: DefaultCellHeader,
    sortDirection: "desc",
    get tooltip() {
        return translate("segment.analysis.table.column.creationdate.tooltip");
    },
    visible: true,
    minWidth: 160,
};
const userName = {
    field: "userName",
    get displayName() {
        return translate("segment.analysis.org.table.column.username");
    },
    type: "string",
    sortable: true,
    isSorted: true,
    cellComponent: ({ value, row }) => {
        const isBlacklisted = !!row.isBlacklisted;

        const compToRender = () => (
            <DateModifiedWrapper onClick={() => onLinkRowClick(row)}>
                <DefaultCell className={isBlacklisted ? "segments-table-row-disabled" : ""}>
                    {<Capitalize>{value}</Capitalize>}
                    {itsMyOwnSegment(row.userId) ? (
                        <OwnerLabel>
                            <I18n>segment.analysis.org.table.column.username.my.segment</I18n>
                        </OwnerLabel>
                    ) : null}
                </DefaultCell>
            </DateModifiedWrapper>
        );

        return isBlacklisted ? <BlacklistedTooltip>{compToRender()}</BlacklistedTooltip> : compToRender();
    },
    headerComponent: DefaultCellHeader,
    sortDirection: "desc",
    get tooltip() {
        return translate("segment.analysis.org.table.column.username.tooltip");
    },
    visible: true,
    width: 152,
};

const CustomSegmentsTableColumnsConfig = (supportSelectAll) => [
    rowSelection(supportSelectAll),
    index,
    domain,
    lastUpdated,
];

const CustomSegmentsOrgTableColumnsConfig = (supportSelectAll) => [
    rowSelection(supportSelectAll),
    {
        ...index,
        cellComponent: ({ row, tableOptions }) => {
            const isBlacklisted = row.isBlacklisted && itsMyOwnSegment(row.userId);
            const currentPage = tableOptions?.customPager?.currentPage;
            const index = getIndex(row.index, currentPage, ROWS_PER_PAGE);
            return <div className={`u-alignCenter ${isBlacklisted ? "segments-table-row-disabled" : ""}`}>{index}</div>;
        },
    },
    { ...domain, width: 276 },
    { ...lastUpdated, minWidth: 152 },
    userName,
];

export const CustomSegmentsTableColumnsConfigGen = (
    sortbyField: string,
    sortDirection: string,
    organizationSegments = false,
) => {
    const supportSelectAll = !organizationSegments;
    const columns: any = organizationSegments
        ? CustomSegmentsOrgTableColumnsConfig(supportSelectAll)
        : CustomSegmentsTableColumnsConfig(supportSelectAll);
    return columns.map((col, idx) => {
        if (!col.sortable) {
            return col;
        }
        return {
            ...col,
            isSorted: col.field === sortbyField,
            sortDirection: col.field === sortbyField ? sortDirection : col.sortDirection,
        };
    });
};

export const segmentsTableOptions = (
    setIsSharingModalOpen?: (isOpen: boolean) => void,
    onChangeExternalModalState?: (isOpen: boolean) => void,
    onPickSegment?: (segmentId: ICustomSegment) => void,
    isLimitationExceeded?: boolean,
) => {
    const segmentApiService = new SegmentsApiService();

    const duplicateSegmentClick = (row) => {
        if (isLimitationExceeded) {
            showSegmentsMeteringHook();
            return;
        }

        SwTrack.all.trackEvent("Internal Link", "click", `segmentAnalysis/duplicateSegment/${row.id}`);

        const segmentWizardStateName = getSegmentWizardRoute(proNavigator.getCurrentModule());
        proNavigator.go(segmentWizardStateName, { sid: row.id, createNew: true });
    };
    const editSegmentClick = (row) => {
        SwTrack.all.trackEvent("Internal Link", "click", `segmentAnalysis/editSegment/${row.id}`);
        const segmentWizardStateName = getSegmentWizardRoute(proNavigator.getCurrentModule());
        proNavigator.go(segmentWizardStateName, { sid: row.id });
    };

    const onDeleteSegmentClick = async (row) => {
        try {
            await segmentApiService.deleteCustomSegment(row.id);
            const customAttributes = resolveTrackingCustomAttributesBase(row);
            allTrackers.dispatchTrackEvent("Button", "click", "deleteSegment", customAttributes);
            loadCustomSegmentsMetadata(true); // reload segments metadata
        } catch (e) {}
    };

    const onCopySegmentToUser = (row) => {
        const customAttributes = resolveTrackingCustomAttributesBase(row);
        allTrackers.dispatchTrackEvent("Button", "click", "Copy to user/menu", customAttributes);
        onChangeExternalModalState(true);
        onPickSegment(row);
    };

    const onClickShare = (segment) => {
        setIsSharingModalOpen(true);
        onPickSegment(segment);
    };

    const analyseButton = ({ row = undefined }) => {
        const isBlacklisted = !!row?.isBlacklisted;
        const isDisabled = row?.segmentAdvancedDisabled || isBlacklisted;
        const buttonElem = (
            <AnalyzeLable
                onClick={() => onLinkRowClick(row)}
                isDisabled={isDisabled}
            >
                {translate("conversion.home.leaderboard.table.action.analyse")}
            </AnalyzeLable>
        );

        if (isBlacklisted) {
            return <div />;
        }

        if (isDisabled) {
            return (
                <PlainTooltip
                    placement="top"
                    tooltipContent={translate("segment.analysis.table.column.segment.advanced.disabled")}
                >
                    <div>{buttonElem}</div>
                </PlainTooltip>
            );
        }
        return buttonElem;
    };
    const ellipsis = ({ row }) => (
        <SegmentsStartTableAdditionalOptionsEllipsis
            row={row}
            onClickDelete={onDeleteSegmentClick}
            onClickDuplicate={duplicateSegmentClick}
            onClickEdit={editSegmentClick}
            onCopySegmentToUser={onCopySegmentToUser}
            onClickShare={onClickShare}
            isLimitationExceeded={isLimitationExceeded}
        />
    );

    const deleteBtn = ({ row }) => (
        <IconButton
            type="flat"
            iconName="delete"
            onClick={() => {
                onDeleteSegmentClick(row);
            }}
        />
    );

    // if the row is disabled then only a delete button will be shown, otherwise the ellipsis button will be shown:
    const actionButton = (props) => {
        const { row } = props;
        const isBlacklisted = !!row?.isBlacklisted;
        const itsMySegment = itsMyOwnSegment(row?.userId);

        // if blacklisted and its not my segment the an empty btn will be rendered:
        if (isBlacklisted && !itsMySegment) {
            return <EmptyBtn />;
        }

        return isBlacklisted && itsMySegment ? deleteBtn({ ...props }) : ellipsis({ ...props });
    };

    return {
        rowActionsComponents: [analyseButton, actionButton],
    };
};
